import React, { FC } from "react";
import { BOARD_COLORS, LEG_COLOR } from "./palette";

export type MarkKind = "vehicle" | "pickup" | "dropoff" | "hub";

/**
 * The four marks the board draws, as SVG at an arbitrary centre.
 *
 * Shape carries the trip's stage -- filled dot = the van, hollow triangle = a pickup, hollow
 * square = a dropoff, diamond = a hub -- so the same four shapes have to appear in the legend and
 * on the filter chips as on the map, or the legend is describing a different picture. One
 * definition, used by both.
 */
export const Mark: FC<{ kind: MarkKind; x: number; y: number; r?: number; ring?: string }> = ({
  kind,
  x,
  y,
  r = 5.5,
  ring = BOARD_COLORS.ring,
}) => {
  switch (kind) {
    case "vehicle":
      return <circle cx={x} cy={y} r={r} fill="currentColor" stroke={ring} strokeWidth={2} />;
    case "pickup":
      return (
        <path
          d={`M${x} ${y - r * 1.3}L${x + r * 1.13} ${y + r * 0.65}L${x - r * 1.13} ${y + r * 0.65}Z`}
          fill={ring}
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      );
    case "dropoff":
      return (
        <rect x={x - r} y={y - r} width={r * 2} height={r * 2} fill={ring} stroke="currentColor" strokeWidth={2} />
      );
    default:
      return (
        <g>
          <path
            d={`M${x} ${y - r}L${x + r} ${y}L${x} ${y + r}L${x - r} ${y}Z`}
            fill={ring}
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
          <circle cx={x} cy={y} r={1.6} fill="currentColor" />
        </g>
      );
  }
};

const TARGET_OF_LEG: Record<string, MarkKind> = {
  to_pickup: "pickup",
  to_dropoff: "dropoff",
  return: "hub",
};

/**
 * The little "van -> next stop" glyph beside a chip or legend row.
 *
 * A pending trip has no van, so it is drawn as the lone pickup triangle it appears as on the map.
 * Everything else is the van dot, the dashed line and the mark it is heading to -- which is the
 * only thing distinguishing the two green legs from each other.
 */
const LegGlyph: FC<{ leg: string; color?: string; title?: string }> = ({ leg, color, title }) => {
  const style = { color: color || LEG_COLOR[leg] || BOARD_COLORS.return };
  if (leg === "pending" || leg.indexOf("pending") === 0) {
    return (
      <svg viewBox="0 0 30 14" width={30} height={14} style={style} aria-hidden={!title} role={title ? "img" : null}>
        {title ? <title>{title}</title> : null}
        <Mark kind="pickup" x={15} y={7} r={4.5} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 30 14" width={30} height={14} style={style} aria-hidden={!title} role={title ? "img" : null}>
      {title ? <title>{title}</title> : null}
      <path
        d="M10 7 H19"
        stroke="currentColor"
        strokeWidth={1.25}
        strokeDasharray={leg === "return" ? "2 3" : "3 2.5"}
        opacity={0.75}
      />
      <Mark kind="vehicle" x={5} y={7} r={4.5} />
      <Mark kind={TARGET_OF_LEG[leg] || "dropoff"} x={24} y={7} r={4.5} />
    </svg>
  );
};

export default LegGlyph;
